import { useState } from 'react';
const fmt = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n || 0);

export default function ProfitCalculator() {
  const [form, setForm] = useState({
    selling_price: '',
    fba_fee: '',
    referral_fee: '',
    digital_service_fee: '0.06',
    cogs: '',
    ppc_per_unit: '0',
    units: '1',
  });
  const [result, setResult] = useState(null);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  // Auto calculate VAT on fees (20%)
  const fba_vat = (parseFloat(form.fba_fee) || 0) * 0.20;
  const referral_vat = (parseFloat(form.referral_fee) || 0) * 0.20;
  const dsf_vat = (parseFloat(form.digital_service_fee) || 0) * 0.20;

  const calculate = () => {
    const sp = parseFloat(form.selling_price) || 0;
    const fba = parseFloat(form.fba_fee) || 0;
    const ref = parseFloat(form.referral_fee) || 0;
    const dsf = parseFloat(form.digital_service_fee) || 0;
    const cogs = parseFloat(form.cogs) || 0;
    const ppc = parseFloat(form.ppc_per_unit) || 0;
    const units = parseInt(form.units) || 1;

    // Strip VAT from selling price
    const vat_collected = sp / 6;
    const net_revenue = sp - vat_collected;

    // VAT on fees (auto calculated at 20%)
    const total_vat_reclaimable = fba_vat + referral_vat + dsf_vat;

    // Net profit
    const net_profit = net_revenue - fba - ref - dsf + total_vat_reclaimable - cogs - ppc;

    // VAT due to HMRC
    const vat_due_hmrc = vat_collected - total_vat_reclaimable;

    const margin = net_revenue > 0 ? ((net_profit / net_revenue) * 100) : 0;

    setResult({
      per_unit: {
        net_profit: net_profit.toFixed(2),
        vat_due_hmrc: vat_due_hmrc.toFixed(2),
        margin: margin.toFixed(1),
      },
      total: {
        gross_sales: (sp * units).toFixed(2),
        vat_collected: (vat_collected * units).toFixed(2),
        net_revenue: (net_revenue * units).toFixed(2),
        fba_fees: (fba * units).toFixed(2),
        fba_vat_reclaimable: (fba_vat * units).toFixed(2),
        referral_fees: (ref * units).toFixed(2),
        referral_vat_reclaimable: (referral_vat * units).toFixed(2),
        digital_service_fees: (dsf * units).toFixed(2),
        digital_service_vat_reclaimable: (dsf_vat * units).toFixed(2),
        total_vat_reclaimable: (total_vat_reclaimable * units).toFixed(2),
        cogs: (cogs * units).toFixed(2),
        ppc_spend: (ppc * units).toFixed(2),
        net_profit: (net_profit * units).toFixed(2),
        vat_due_hmrc: (vat_due_hmrc * units).toFixed(2),
      },
      units,
    });
  };

  const card = { background: '#fff', border: '0.5px solid rgba(0,0,0,0.08)', borderRadius: '12px', padding: '1.25rem', marginBottom: '12px' };
  const inp = { width: '100%', padding: '8px 10px', border: '0.5px solid #ddd', borderRadius: '7px', fontSize: '13px', boxSizing: 'border-box' };
  const inpAuto = { ...inp, background: '#f5f5f5', color: '#888' };
  const label = { display: 'block', fontSize: '11px', fontWeight: '500', color: '#666', marginBottom: '4px' };
  const labelAuto = { ...label, color: '#aaa' };

  return (
    <div>
      <div style={card}>
        <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '1rem' }}>Enter your product details</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>

          <div>
            <label style={label}>Selling price (VAT inclusive) £</label>
            <input style={inp} type="number" step="0.01" value={form.selling_price} onChange={set('selling_price')} placeholder="6.99" />
          </div>
          <div>
            <label style={label}>Units sold</label>
            <input style={inp} type="number" value={form.units} onChange={set('units')} placeholder="100" />
          </div>

          <div>
            <label style={label}>FBA fee £</label>
            <input style={inp} type="number" step="0.01" value={form.fba_fee} onChange={set('fba_fee')} placeholder="2.65" />
          </div>
          <div>
            <label style={labelAuto}>VAT on FBA (auto 20%) £</label>
            <input style={inpAuto} type="number" value={fba_vat.toFixed(2)} readOnly />
          </div>

          <div>
            <label style={label}>Referral fee £</label>
            <input style={inp} type="number" step="0.01" value={form.referral_fee} onChange={set('referral_fee')} placeholder="0.56" />
          </div>
          <div>
            <label style={labelAuto}>VAT on referral (auto 20%) £</label>
            <input style={inpAuto} type="number" value={referral_vat.toFixed(2)} readOnly />
          </div>

          <div>
            <label style={label}>Digital service fee £</label>
            <input style={inp} type="number" step="0.01" value={form.digital_service_fee} onChange={set('digital_service_fee')} />
          </div>
          <div>
            <label style={labelAuto}>VAT on DSF (auto 20%) £</label>
            <input style={inpAuto} type="number" value={dsf_vat.toFixed(2)} readOnly />
          </div>

          <div>
            <label style={label}>COGS per unit £</label>
            <input style={inp} type="number" step="0.01" value={form.cogs} onChange={set('cogs')} placeholder="1.40" />
          </div>
          <div>
            <label style={label}>PPC spend per unit £</label>
            <input style={inp} type="number" step="0.01" value={form.ppc_per_unit} onChange={set('ppc_per_unit')} placeholder="0.20" />
          </div>

        </div>
        <button onClick={calculate}
          style={{ width: '100%', marginTop: '1rem', padding: '11px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
          Calculate profit & VAT
        </button>
      </div>

      {result && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            {[
              { label: 'Net profit per unit', value: fmt(result.per_unit.net_profit), type: parseFloat(result.per_unit.net_profit) >= 0 ? 'pos' : 'neg' },
              { label: 'Margin', value: `${result.per_unit.margin}%`, type: parseFloat(result.per_unit.margin) >= 0 ? 'pos' : 'neg' },
              { label: `Total net profit (${result.units} units)`, value: fmt(result.total.net_profit), type: parseFloat(result.total.net_profit) >= 0 ? 'pos' : 'neg' },
              { label: 'VAT due to HMRC', value: fmt(result.total.vat_due_hmrc), type: 'neg' },
            ].map(m => (
              <div key={m.label} style={{ background: m.type === 'pos' ? '#EAF3DE' : '#FCEBEB', borderRadius: '10px', padding: '14px 16px' }}>
                <div style={{ fontSize: '11px', color: '#777', marginBottom: '5px', fontWeight: '500' }}>{m.label}</div>
                <div style={{ fontSize: '22px', fontWeight: '500', color: m.type === 'pos' ? '#1D9E75' : '#E24B4A' }}>{m.value}</div>
              </div>
            ))}
          </div>

          <div style={card}>
            <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '1rem' }}>Full breakdown ({result.units} units)</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <tbody>
                {[
                  { label: 'Gross sales', value: result.total.gross_sales, color: '#1D9E75' },
                  { label: 'VAT collected (÷6)', value: -result.total.vat_collected, color: '#E24B4A' },
                  { label: 'Net revenue (ex VAT)', value: result.total.net_revenue, color: '#1a1a1a', bold: true },
                  { label: 'FBA fees', value: -result.total.fba_fees, color: '#E24B4A' },
                  { label: 'VAT reclaimed on FBA (20%)', value: result.total.fba_vat_reclaimable, color: '#1D9E75' },
                  { label: 'Referral fees', value: -result.total.referral_fees, color: '#E24B4A' },
                  { label: 'VAT reclaimed on referral (20%)', value: result.total.referral_vat_reclaimable, color: '#1D9E75' },
                  { label: 'Digital service fees', value: -result.total.digital_service_fees, color: '#E24B4A' },
                  { label: 'VAT reclaimed on DSF (20%)', value: result.total.digital_service_vat_reclaimable, color: '#1D9E75' },
                  { label: 'COGS', value: -result.total.cogs, color: '#E24B4A' },
                  { label: 'PPC spend', value: -result.total.ppc_spend, color: '#E24B4A' },
                  { label: 'NET PROFIT', value: result.total.net_profit, color: parseFloat(result.total.net_profit) >= 0 ? '#1D9E75' : '#E24B4A', bold: true },
                  { label: 'VAT due to HMRC', value: result.total.vat_due_hmrc, color: '#E24B4A', bold: true },
                ].map((row, i) => (
                  <tr key={i} style={{ borderBottom: '0.5px solid rgba(0,0,0,0.05)' }}>
                    <td style={{ padding: '8px 0', fontWeight: row.bold ? '600' : '400' }}>{row.label}</td>
                    <td style={{ padding: '8px 0', textAlign: 'right', color: row.color, fontWeight: row.bold ? '600' : '400' }}>
                      {parseFloat(row.value) >= 0 ? '+' : ''}{fmt(row.value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}